import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, writeBatch } from '@angular/fire/firestore';
import { Question, SchoolQuestions } from '../_models/question';
import { formatAidYear } from '../_shared/aid-year/aid-year.config';
import { AuthService } from './auth.service';
import { buildCreateSchool, normalizeSchoolCode, schoolCodeError, SchoolContent } from './create-school.builder';
import { normalizeSchoolQuestionSections } from './questions.service';

export const MISSING_DEFAULT_CONTENT_MESSAGE =
  'Default school content is missing in Firestore (defaults/content). Open Default content, save the template, then try again.';

export interface CreateSchoolRequest {
  code: string;
  name: string;
  institutionalMethod: boolean;
  orgId: string;
  aidYear: number;
}

@Injectable({
  providedIn: 'root',
})
export class CreateSchoolService {
  constructor(
    private firestore: Firestore,
    private auth: AuthService,
  ) {}

  async createSchool(request: CreateSchoolRequest): Promise<string> {
    const code = normalizeSchoolCode(request.code);
    const codeProblem = schoolCodeError(code);
    if (codeProblem) {
      throw new Error(codeProblem);
    }
    if (!request.name.trim()) {
      throw new Error('Enter a school name.');
    }
    if (!request.orgId.trim()) {
      throw new Error('Enter an organization ID.');
    }

    const schoolRef = doc(this.firestore, 'schools', code);
    const existing = await getDoc(schoolRef);
    if (existing.exists()) {
      throw new Error('A school with that code already exists.');
    }

    const allQuestions = await this.loadAllQuestions(request.aidYear);
    const user = await this.auth.currentUser();
    const content = await this.loadDefaultContent();
    const now = new Date();
    const documents = buildCreateSchool({
      ...request,
      code,
      content,
      createdAt: now,
      allQuestions,
    });
    normalizeSchoolQuestionSections(documents.questions);

    const schoolQuestions: SchoolQuestions = {
      schoolId: code,
      aidYear: request.aidYear,
      questions: documents.questions,
      lastUpdated: now,
      lastUpdatedBy: user?.uid ?? '',
    };

    const batch = writeBatch(this.firestore);
    batch.set(schoolRef, documents.school);
    batch.set(doc(this.firestore, 'preferences', code), documents.preferences);
    batch.set(doc(this.firestore, 'preferences_draft', code), documents.preferences);
    batch.set(doc(this.firestore, `calcConfig/${code}/calcRules/draft`), documents.calcRules);
    batch.set(doc(this.firestore, `calcConfig/${code}/calcRules/published`), documents.calcRules);
    batch.set(doc(this.firestore, 'questions', code), { questions: documents.questions });
    batch.set(doc(this.firestore, 'questions_draft', code), { questions: documents.questions });
    batch.set(doc(this.firestore, `calcConfig/${code}/questions/${request.aidYear}`), schoolQuestions);
    await batch.commit();

    return code;
  }

  async loadDefaultContent(): Promise<SchoolContent> {
    let snap;
    try {
      snap = await getDoc(doc(this.firestore, 'defaults', 'content'));
    } catch (err) {
      console.error('Unable to read defaults/content', err);
      throw new Error(MISSING_DEFAULT_CONTENT_MESSAGE);
    }
    if (!snap.exists()) {
      throw new Error(MISSING_DEFAULT_CONTENT_MESSAGE);
    }
    const data = snap.data();
    return {
      welcomeIntro: stringField(data, 'welcomeIntro'),
      welcomeDisclaimer: stringField(data, 'welcomeDisclaimer'),
      nextSteps: stringField(data, 'nextSteps'),
      schoolQuestionHeader: stringField(data, 'schoolQuestionHeader'),
    };
  }

  async saveDefaultContent(content: SchoolContent): Promise<void> {
    await setDoc(doc(this.firestore, 'defaults', 'content'), content);
  }

  private async loadAllQuestions(aidYear: number): Promise<Question[]> {
    const snap = await getDoc(doc(this.firestore, 'allQuestions', aidYear.toString()));
    if (!snap.exists()) {
      throw new Error(`No master question list exists for aid year ${formatAidYear(aidYear)}.`);
    }
    const questions = snap.data()['questions'];
    return Array.isArray(questions) ? questions as Question[] : [];
  }
}

function stringField(data: Record<string, unknown>, key: string): string {
  const value = data[key];
  return typeof value === 'string' ? value : '';
}
